import React, {useEffect, useMemo, useState} from "react";
import dayjs from "dayjs";
import {Autocomplete, Box, Divider, Grid, Paper, Stack, Typography, useTheme, useMediaQuery, Chip} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers";
import LogbookApi from "../../../api/staffApi/logbookApi";
import {ICalisanOdemeKayit, IGider} from "../../../constants/types/logbook";
import {useSelector} from "../../../redux/store";

import {DataGrid, GridColDef} from '@mui/x-data-grid';
import {PieChart} from "@mui/x-charts";
import TextField from "@mui/material/TextField";
import FormatNumber from "../../../helpers/formatNumber";
import {ReceiptLong, Payments, PieChart as PieChartIcon, FilterList, CalendarMonth, LocalOffer} from "@mui/icons-material";

const Giderler = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const sabitler = useSelector(state => state.app.sabitler);
    const [giderler, setGiderler] = useState<IGider[]>([]);
    const [personelOdemeleri, setPersonelOdemeleri] = useState<ICalisanOdemeKayit[]>([]);
    const [groupBy, setGroupBy] = useState<"tip" | "kaynak">("tip");

    const [baslangicTarih, setBaslangicTarih] = useState<string>(dayjs().subtract(1, 'month').format('DD-MM-YYYY'));
    const [bitisTarih, setBitisTarih] = useState<string>(dayjs().format('DD-MM-YYYY'));

    // SÜTUN TANIMLARI
    const giderColumns: GridColDef<IGider>[] = [
        {field: 'tarih', headerName: 'Tarih', width: 100},
        {
            field: 'tip',
            headerName: 'Tip',
            valueGetter: (value) => sabitler?.gider_tipleri?.find(q => q.id === value)?.tip ?? "",
            width: 120
        },
        {
            field: 'miktar', 
            headerName: 'Miktar', 
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={700} color="primary.main">
                    {FormatNumber(params.value)} ₺
                </Typography>
            )
        },
        {field: 'aciklama', headerName: 'Açıklama', flex: 1, minWidth: 150},
        {
            field: 'kaynak',
            headerName: 'Kaynak',
            flex: 1,
            minWidth: 120,
            valueGetter: (value) => sabitler?.hesaplar?.find(q => q.id === value)?.hesapAdi ?? "",
        },
    ];

    const personelColumns: GridColDef<ICalisanOdemeKayit>[] = [
        {field: 'tarih', headerName: 'Tarih', width: 100},
        {
            field: 'kisi',
            headerName: 'Personel',
            valueGetter: (value) => {
                const hesap = sabitler?.hesaplar?.find(q => q.id === value);
                return hesap?.hesapAdi ?? "";
            },
            width: 140
        },
        {
            field: 'miktar', 
            headerName: 'Miktar', 
            width: 100,
            renderCell: (params) => (
                <Typography variant="body2" fontWeight={700} color="primary.main">
                    {FormatNumber(params.value)} ₺
                </Typography>
            )
        },
        {field: 'tip', headerName: 'Ödeme Tipi', width: 110},
        {
            field: 'kaynak',
            headerName: 'Kaynak',
            flex: 1,
            minWidth: 120,
            valueGetter: (value) => sabitler?.hesaplar?.find(q => q.id === value)?.hesapAdi ?? "",
        },
    ];

    // HESAPLAMALAR
    const totals = useMemo(() => {
        const gTotal = giderler.reduce((sum, item) => sum + (item.miktar ?? 0), 0);
        const pTotal = personelOdemeleri.reduce((sum, item) => sum + (item.miktar ?? 0), 0);
        return {
            gider: gTotal,
            personel: pTotal,
            genel: gTotal + pTotal
        };
    }, [giderler, personelOdemeleri]);

    const chartsData = useMemo(() => {
        const gMap: Record<string, number> = {};

        // Giderleri grupla
        giderler.forEach(g => {
            const label = (groupBy === "tip"
                ? sabitler?.gider_tipleri?.find(q => q.id === g.tip)?.tip
                : sabitler?.hesaplar?.find(q => q.id === g.kaynak)?.hesapAdi) ?? "Diğer";
            gMap[label] = (gMap[label] || 0) + (g.miktar || 0);
        });

        // Personel ödemelerini grupla
        personelOdemeleri.forEach(p => {
            const label = (groupBy === "tip"
                ? "Personel"
                : sabitler?.hesaplar?.find(q => q.id === p.kaynak)?.hesapAdi) ?? "Diğer";
            gMap[label] = (gMap[label] || 0) + (p.miktar || 0);
        });

        // Listeyi oluştur ve sırala
        const dataList = Object.keys(gMap).map(k => {
            const val = Math.round(gMap[k]);
            return {
                id: k,
                label: k,
                value: val
            };
        });
        const sortedData = dataList.sort((a, b) => b.value - a.value);

        // İlk 10'u al, kalanları grupla
        if (sortedData.length <= 10) return sortedData;

        const top10 = sortedData.slice(0, 10);
        const othersValue = Math.round(sortedData.slice(10).reduce((sum, item) => sum + item.value, 0));

        return [...top10, {
            id: "other-expenses",
            label: "Diğer Harcamalar",
            value: othersValue
        }];
    }, [giderler, personelOdemeleri, groupBy, sabitler]);

    const fetchData = () => {
        if (!!sabitler) {
            LogbookApi.getGiderler({baslangicTarih, bitisTarih}).then((response: any) => {
                setGiderler(response.giderler || []);
                setPersonelOdemeleri(response.personel_odemeleri || []);
            });
        }
    };

    useEffect(() => {
        fetchData();
    }, [sabitler, baslangicTarih, bitisTarih]);

    return (
        <Box sx={{ mt: 1 }}>
            <Grid container spacing={3}>
                {/* FİLTRELER VE ÖZET */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <Grid container spacing={2.5} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={3}>
                                <Stack spacing={0.5} sx={{ mb: 1 }}>
                                    <Typography variant="overline" fontWeight={800} color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CalendarMonth fontSize="small" /> Başlangıç Tarihi
                                    </Typography>
                                    <DatePicker
                                        value={dayjs(baslangicTarih, "DD-MM-YYYY")}
                                        onChange={(val) => setBaslangicTarih(val?.format("DD-MM-YYYY") ?? "")}
                                        slotProps={{textField: {fullWidth: true, size: 'small', sx: { borderRadius: '10px' }}}}
                                    />
                                </Stack>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <Stack spacing={0.5} sx={{ mb: 1 }}>
                                    <Typography variant="overline" fontWeight={800} color="textSecondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <CalendarMonth fontSize="small" /> Bitiş Tarihi
                                    </Typography>
                                    <DatePicker
                                        value={dayjs(bitisTarih, "DD-MM-YYYY")}
                                        onChange={(val) => setBitisTarih(val?.format("DD-MM-YYYY") ?? "")}
                                        slotProps={{textField: {fullWidth: true, size: 'small', sx: { borderRadius: '10px' }}}}
                                    />
                                </Stack>
                            </Grid>
                            
                            <Grid item xs={12} md={6}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={4}>
                                        <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'white', borderRadius: '12px', textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontWeight: 700 }}>Toplam Gider</Typography>
                                            <Typography variant="h6" fontWeight={900}>{FormatNumber(totals.genel)} ₺</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Box sx={{ p: 2, bgcolor: 'primary.light', color: 'primary.dark', borderRadius: '12px', textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontWeight: 700 }}>Harcamalar</Typography>
                                            <Typography variant="h6" fontWeight={900}>{FormatNumber(totals.gider)} ₺</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6} sm={4}>
                                        <Box sx={{ p: 2, bgcolor: 'secondary.light', color: 'secondary.dark', borderRadius: '12px', textAlign: 'center' }}>
                                            <Typography variant="caption" sx={{ opacity: 0.8, display: 'block', fontWeight: 700 }}>Personel</Typography>
                                            <Typography variant="h6" fontWeight={900}>{FormatNumber(totals.personel)} ₺</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>

                {/* GRAFİK ANALİZİ */}
                <Grid item xs={12}>
                    <Paper sx={{ p: 3, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ mb: 3, gap: 2 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <PieChartIcon color="primary" />
                                <Typography variant="h6" sx={{fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1}}>Harcama Dağılımı</Typography>
                            </Stack>
                            <Stack direction="row" spacing={2} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
                                <FilterList fontSize="small" color="action" />
                                <Autocomplete
                                    size="small"
                                    sx={{width: { xs: '100%', sm: 200 }}}
                                    options={["tip", "kaynak"]}
                                    getOptionLabel={(opt) => opt === "tip" ? "Gider Tipi" : "Ödeme Kaynağı"}
                                    value={groupBy}
                                    onChange={(_, v) => v && setGroupBy(v as any)}
                                    renderInput={(params) => <TextField {...params} label="Gruplama" sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px' } }}/>}
                                    disableClearable
                                />
                            </Stack>
                        </Stack>
                        
                        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', minHeight: 350 }}>
                            <PieChart
                                series={[{
                                    data: chartsData,
                                    innerRadius: 60,
                                    paddingAngle: 3,
                                    cornerRadius: 6,
                                    cx: isMobile ? 150 : 180,
                                    highlightScope: {faded: 'global', highlighted: 'item'},
                                    valueFormatter: (v) => `${FormatNumber(v.value)} ₺ (%${((v.value / (totals.genel || 1)) * 100).toFixed(1)})`,
                                }]}
                                slotProps={{
                                    legend: {
                                        direction: isMobile ? 'row' : 'column',
                                        position: isMobile ? {vertical: 'bottom', horizontal: 'middle'} : {vertical: 'middle', horizontal: 'right'},
                                        itemMarkWidth: 12,
                                        itemMarkHeight: 12,
                                        markGap: 8,
                                        itemGap: isMobile ? 15 : 12,
                                        labelStyle: {fontSize: 12, fontWeight: 700}
                                    }
                                }}
                                height={isMobile ? 500 : 350}
                            />
                        </Box>
                    </Paper>
                </Grid>

                {/* DETAY TABLOLAR */}
                <Grid item xs={12} lg={6}>
                    <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, px: 1 }}>
                            <LocalOffer color="primary" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Harcamalar Detayı</Typography>
                        </Stack>
                        <Box sx={{ height: 500, width: '100%' }}>
                            <DataGrid 
                                rows={giderler} 
                                columns={giderColumns} 
                                pageSizeOptions={[10, 50]}
                                density="compact"
                                sx={{ 
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', borderRadius: '8px' },
                                    '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(0,0,0,0.05)' }
                                }}
                            />
                        </Box>
                    </Paper>
                </Grid>
                
                <Grid item xs={12} lg={6}>
                    <Paper sx={{ p: 2, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2, px: 1 }}>
                            <Payments color="primary" fontSize="small" />
                            <Typography variant="subtitle1" fontWeight={800} sx={{ textTransform: 'uppercase' }}>Personel Ödemeleri</Typography>
                        </Stack>
                        <Box sx={{ height: 500, width: '100%' }}>
                            <DataGrid 
                                rows={personelOdemeleri} 
                                columns={personelColumns} 
                                pageSizeOptions={[10, 50]}
                                density="compact"
                                sx={{ 
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': { bgcolor: 'grey.50', borderRadius: '8px' },
                                    '& .MuiDataGrid-cell': { borderBottom: '1px solid rgba(0,0,0,0.05)' }
                                }}
                            />
                        </Box>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Giderler;
